import type { GameMap } from '@unmatched/engine';
import { Application } from 'pixi.js';
import { useEffect, useRef } from 'react';

import type { BoardCallbacks, BoardEffect, BoardView } from './renderer.js';
import { BoardRenderer } from './renderer.js';

interface Props {
  map: GameMap;
  view: BoardView;
  callbacks: BoardCallbacks;
  /** Jednorazové efekty (plávajúce čísla). Renderer si pamätá, ktoré už prehral. */
  effects?: readonly BoardEffect[];
}

/**
 * Zrušenie plátna BEZ uvoľnenia globálnych zdrojov Pixi. `destroy(true)` by
 * vyprázdnilo zdieľaný pool textúr textov — a druhé plátno, ktoré ešte žije
 * (React v dev režime mountuje dvakrát, v hre sa plátno mení s mapou), by pri
 * najbližšej zmene čísla zdravia spadlo v `TexturePool.returnTexture`.
 */
const DESTROY_VIEW = { removeView: true, releaseGlobalResources: false } as const;

export function BoardCanvas({ map, view, callbacks, effects = [] }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<BoardRenderer | null>(null);
  // Callbacky sa menia s každým renderom Reactu — renderer si drží stabilnú
  // referenciu a číta cez ňu vždy tie aktuálne.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const viewRef = useRef(view);
  viewRef.current = view;
  const effectsRef = useRef(effects);
  effectsRef.current = effects;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let renderer: BoardRenderer | null = null;
    let app: Application | null = null;
    let cancelled = false;

    void (async () => {
      const created = new Application();
      await created.init({
        // Priehľadné plátno — bodkovaná mriežka pod doskou je CSS (`.boardWrap`).
        backgroundAlpha: 0,
        antialias: true,
        resizeTo: host,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      if (cancelled) {
        created.destroy(DESTROY_VIEW);
        return;
      }
      app = created;
      host.appendChild(created.canvas);
      renderer = new BoardRenderer(created, map, {
        onSpaceClick: (id) => callbacksRef.current.onSpaceClick(id),
        onFighterClick: (id) => callbacksRef.current.onFighterClick(id),
      });
      rendererRef.current = renderer;
      renderer.update(viewRef.current);
      renderer.play(effectsRef.current);
    })();

    return () => {
      cancelled = true;
      renderer?.destroy();
      rendererRef.current = null;
      app?.destroy(DESTROY_VIEW, { children: true });
    };
  }, [map]);

  useEffect(() => {
    rendererRef.current?.update(view);
  }, [view]);

  useEffect(() => {
    rendererRef.current?.play(effects);
  }, [effects]);

  return <div className="board-canvas" ref={hostRef} />;
}
